// Shared Highcharts styling for the two Market Health charts (ScoreChart and
// StrategyChart). Both charts previously set their `chart`/`xAxis`/`yAxis`/
// `tooltip`/`credits` options independently, so they drifted apart visually
// (different heights, tooltip chrome, axis label colors, gridlines). This
// module centralizes the common LOOK so the two charts read as a matched
// pair; each chart still supplies its own functional bits (plain line vs.
// stock chart, plot bands, zones, navigator/range selector) on top of it.
//
// Only presentation lives here — nothing data-shaped or chart-type-specific.

import {
  getRangeSelector,
  getCompactRangeSelectorOptions,
  RANGE_SELECTOR_BUTTONS,
} from '../FunViz/config/rangeSelectorConfig';

// Market Health defaults its stock charts to the 5-year window (the Analysis
// pages default to 15y via getRangeSelector's own default). Derived from the
// shared button list rather than hardcoded so it can't drift if the list
// changes. Falls back to 0 (the first/shortest button) if 5y is ever removed.
const FIVE_YEAR_BUTTON_INDEX = Math.max(
  0,
  RANGE_SELECTOR_BUTTONS.findIndex((b) => b.type === 'year' && b.count === 5),
);

// The page's shared text palette (matches MarketHealth.module.css: #2c3e50
// headings, #6c757d muted, #e9ecef borders/gridlines).
const AXIS_LABEL_COLOR = '#6c757d';
const AXIS_TITLE_COLOR = '#2c3e50';
const GRIDLINE_COLOR = '#e9ecef';
const FONT_FAMILY =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

// A single chart height for both charts so they line up as sibling cards.
export const CHART_HEIGHT = 340;

// Shared Highcharts dataGrouping config for the Market Health charts. The
// underlying history is DAILY (~8400 trading days back to 1993), so at the
// default multi-year zoom that's far more points than pixels. dataGrouping is
// a purely VISUAL downsample: Highcharts buckets the daily points into the
// coarsest unit that keeps groups at least `groupPixelWidth` px apart —
// week, then month, etc. — and switches back toward daily as you zoom in.
// The raw daily data is never mutated; only what's drawn changes.
//
// `approximation: 'high'` for line series takes the group's max value (rather
// than 'average'), so score peaks / price highs aren't smoothed away — the
// grouped line still traces the envelope of the daily series. Consumers whose
// grouping must preserve a different statistic (e.g. drawdown troughs) pass
// their own `approximation` on top of this.
//
// `units` caps grouping at weekly as the FINEST grouped unit and allows
// month/quarter/year as you zoom further out — matching the "switches to
// weekly when there are too many points" intent while still collapsing
// decades of history sensibly.
export const WEEKLY_DATA_GROUPING = {
  enabled: true,
  groupPixelWidth: 8,
  approximation: 'high',
  units: [
    ['week', [1]],
    ['month', [1, 3, 6]],
    ['year', [1]],
  ],
};

// Shared axis presentation (label color/size, title color/size, gridlines).
// Spread into each chart's own xAxis/yAxis alongside its functional options
// (datetime type, min/max, plotBands, etc.).
export const baseXAxis = {
  type: 'datetime',
  lineColor: GRIDLINE_COLOR,
  tickColor: GRIDLINE_COLOR,
  labels: { style: { color: AXIS_LABEL_COLOR, fontSize: '11px' } },
};

export const baseYAxis = {
  gridLineColor: GRIDLINE_COLOR,
  labels: { style: { color: AXIS_LABEL_COLOR, fontSize: '11px' } },
  title: { style: { color: AXIS_TITLE_COLOR, fontSize: '12px' } },
};

// Shared non-data chart chrome. Each chart merges this first, then overrides
// only what genuinely differs (e.g. StrategyChart re-enables the legend and
// its shared stock tooltip; ScoreChart keeps the legend off).
export const baseChartOptions = {
  chart: {
    height: CHART_HEIGHT,
    style: { fontFamily: FONT_FAMILY },
    spacing: [12, 12, 12, 12],
    zoomType: 'x',
  },
  title: { text: undefined },
  credits: { enabled: false },
  tooltip: {
    backgroundColor: '#fff',
    borderColor: GRIDLINE_COLOR,
    borderRadius: 6,
    borderWidth: 1,
    shadow: false,
    style: { color: AXIS_TITLE_COLOR, fontSize: '12px' },
  },
  legend: {
    enabled: false,
    itemStyle: { color: AXIS_TITLE_COLOR, fontWeight: '600', fontSize: '12px' },
  },
};

// Range selector for both Market Health stock charts (ScoreChart and
// StrategyChart) now consumes the SAME period set as the Analysis main/ratio
// charts (see FunViz/config/rangeSelectorConfig.js): 5y/10y/15y/20y/25y/30y/
// All, defaulting to 15y. Market Health history goes back to 1993 (30+
// years), so the Analysis periods fit — and sharing one list keeps the two
// pages' range buttons identical rather than maintaining a separate shorter
// set here. `getRangeSelector(selected)` supplies the shared `{ selected,
// buttons }`; Market Health passes the 5y index so its charts default to a
// 5-year window (vs the Analysis pages' 15y), which reads better for the
// score history. This wrapper also adds Market Health's own `enabled: true`
// (so the selector shows at all) and `inputEnabled: false` (drop Highcharts'
// free-text date inputs to keep the button row uncluttered — matching the
// Analysis charts' mobile treatment). Returns a fresh object each call so
// Highcharts can merge/mutate one chart's options without affecting the other.
export const getMarketHealthRangeSelector = () => ({
  enabled: true,
  inputEnabled: false,
  ...getRangeSelector(FIVE_YEAR_BUTTON_INDEX),
});

// --- Responsive chart sizing (shared by the Market Health stock charts) ---
//
// Mirrors the Analysis page's approach (FunViz/config/chartConfig.js): use
// Highcharts' native `responsive.rules` to swap in a fixed, legible chart
// height on small screens rather than a width-relative percentage that
// collapses the plot to a sliver once the container is narrow. The desktop
// (default) height is whatever each chart passes via `chart.height`; these
// rules only override it below the breakpoints.
//
// Landscape phones need their own rule (width alone can't distinguish a
// landscape phone from a small laptop) — same 1-line `window.innerHeight`
// check the Analysis config files each carry inline. Duplicated here (rather
// than imported) for the same reason those files duplicate it: it's a trivial
// SSR-safe guard and avoids a cross-module dependency just for one predicate.
const isShortLandscapeViewport = () =>
  typeof window !== 'undefined' && window.innerHeight <= 500;

/**
 * Builds `responsive.rules` for a Market Health stock chart. `mobileHeight`
 * applies at ≤768px (phones / small tablets); `landscapeHeight` applies to
 * short landscape phones. Both also pull in the shared compact range-selector
 * styling and shrink axis labels / grow the navigator touch handles, matching
 * the Analysis charts. Callers pass per-chart target heights so the stacked
 * "Both" layout can keep its price/score height ratio at every breakpoint.
 * `withControls` gates the range-selector + navigator tweaks so the
 * controls-less bottom pane doesn't try to style chrome it doesn't render.
 */
export const getMarketHealthResponsiveRules = (mobileHeight, landscapeHeight, withControls) => ({
  rules: [
    {
      condition: { maxWidth: 768 },
      chartOptions: {
        chart: { height: mobileHeight, spacingLeft: 4, spacingRight: 4 },
        xAxis: { labels: { style: { fontSize: '10px' } } },
        yAxis: { labels: { style: { fontSize: '10px' } } },
        ...(withControls
          ? {
              rangeSelector: getCompactRangeSelectorOptions(),
              navigator: { handles: { width: 10, height: 18 } },
            }
          : {}),
      },
    },
    {
      condition: {
        minWidth: 601,
        maxWidth: 1024,
        callback: () => isShortLandscapeViewport(),
      },
      chartOptions: {
        chart: { height: landscapeHeight },
        ...(withControls ? { navigator: { height: 26 } } : {}),
      },
    },
  ],
});

// Custom range-preset buttons (spec §8) shared by StrategyChart and
// DrawdownChart. Highcharts Stock's native `rangeSelector.buttons` only
// supports relative ("N years back") ranges, so these fixed-date windows —
// deliberately including the unflattering ones (2018 Vol, 2022 Bear) rather
// than only the flattering 2020 COVID rebound — are applied via
// `chart.xAxis[0].setExtremes(min, max)` from a custom button row instead,
// with the built-in `rangeSelector` disabled (`{ enabled: false }`) on both
// charts in favor of it. `getExtremes()` returns `[min, max]`, where `[null,
// null]` (the 'All' preset) means "clear the extremes" — callers should pass
// through as `undefined` for Highcharts' `setExtremes`, which is why this
// returns `null` rather than omitting the tuple entry.
// Presets are ordered chronologically (oldest crash first) after 'All', so
// the button row reads as a timeline of the major drawdowns on record.
//
// Each crash window is anchored to the strategy's own EXIT→RE-ENTRY round trip
// for that episode — the point of the chart is showing the model going to cash
// and coming back — sized to the WIDEST round trip across all three modes
// (adaptive/fullCycle/conservative) so whichever mode is selected, its full
// exit→re-entry stays in frame, then padded a few months before the earliest
// exit and a few months after the latest re-entry. Verified against Market
// Health's own daily history (earliest exit / latest re-entry across modes),
// with Dot-com and GFC given a wider 6-months-each-side pad so the full run-up
// and recovery are visible:
//   - Dot-com:  exit 2000-10-11  re-entry 2003-06-11  -> window 2000-01..2004-03
//   - GFC:      exit 2007-11-15  re-entry 2009-10-05  -> window 2007-02..2010-07
//   - 2018 Vol: exit 2018-12-06  re-entry 2019-02-22  -> window 2018-09..2019-05
//   - COVID:    exit 2020-02-26  re-entry 2020-08-31  -> window 2019-12..2020-12
//   - 2022 Bear:exit 2022-04-06  re-entry 2023-04-24  -> window 2022-01..2023-07
//
// `subset: true` marks every preset except 'All'. Consumers FILTER the
// underlying data to the window (not just zoom the axis), so the navigator/
// scrollbar span only that window — the user can pan/zoom WITHIN it but can't
// scroll back out to full history. On StrategyChart the filtered series are
// re-based to START_VALUE at the subset's first point, so both lines start
// together at the same value for that window. The 'Trailing NY' presets behave
// the same way, just with relative bounds (N years back .. today) instead of
// fixed crash dates. Only 'All' is unfiltered — the full dataset, day-one
// indexing, free exploration.
//
// `getExtremes()` returns `[min, max]`, where `[null, null]` (the 'All'
// preset) means "clear the extremes" — callers pass through as `undefined`
// for Highcharts' `setExtremes`, which is why this returns `null` rather than
// omitting the tuple entry. For `subset` presets, `[min, max]` doubles as the
// filter bounds.
const trailingYears = (years) => {
  const now = Date.now();
  return [now - years * 365.25 * 86400000, now];
};

// Each preset carries a STABLE `key` (never the display `text`, which may be
// reworded) — this is the shared contract the backend keys its precomputed
// per-preset windowed stats by (strategy[mode][lag].windows[key], see
// market-health-compute/index.mjs's PRESET_WINDOWS). The backend replicates
// the SAME window bounds under the SAME keys (it can't import this file); a
// guard test asserts the two key sets stay in sync. `text` remains the button
// label; `subset`/`getExtremes` are unchanged (the chart still filters the
// weekly series by these bounds — only the stats TABLE reads the backend's
// daily-accurate precomputed windows).
export const RANGE_PRESETS = [
  { key: 'all', text: 'All', getExtremes: () => [null, null] },
  { key: 'dotcom', text: 'Dot-com', subset: true, getExtremes: () => [Date.UTC(2000, 0, 1), Date.UTC(2004, 2, 31)] },
  { key: 'gfc', text: 'GFC', subset: true, getExtremes: () => [Date.UTC(2007, 1, 1), Date.UTC(2010, 6, 31)] },
  { key: 'vol2018', text: '2018 Vol', subset: true, getExtremes: () => [Date.UTC(2018, 8, 1), Date.UTC(2019, 4, 31)] },
  { key: 'covid', text: '2020 COVID', subset: true, getExtremes: () => [Date.UTC(2019, 11, 1), Date.UTC(2020, 11, 31)] },
  { key: 'bear2022', text: '2022 Bear', subset: true, getExtremes: () => [Date.UTC(2022, 0, 1), Date.UTC(2023, 6, 31)] },
  { key: 'trailing5y', text: 'Trailing 5Y', subset: true, getExtremes: () => trailingYears(5) },
  { key: 'trailing10y', text: 'Trailing 10Y', subset: true, getExtremes: () => trailingYears(10) },
  { key: 'trailing15y', text: 'Trailing 15Y', subset: true, getExtremes: () => trailingYears(15) },
  { key: 'trailing20y', text: 'Trailing 20Y', subset: true, getExtremes: () => trailingYears(20) },
];
